import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useTreatmentOverview } from "@/hooks/useTreatmentOverview";
import { TreatmentOverviewHeader } from "./TreatmentOverviewHeader";
import { TreatmentOverviewFilters } from "./TreatmentOverviewFilters";
import { TreatmentOverviewTable } from "./TreatmentOverviewTable";
import { PdfViewerDialog } from "./PdfViewerDialog";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { TreatmentOverviewItem } from "@/types/treatment-overview";

interface TreatmentOverviewHomeProps {
  appointmentId?: string;
  patientId?: string;
  onOpenDocumentTab?: (doc: {
    id: string;
    description: string;
    file: string;
  }) => void;
  showHeader?: boolean;
  embedded?: boolean;
}

export const TreatmentOverviewHome = ({
  appointmentId,
  patientId,
  onOpenDocumentTab,
  showHeader = true,
  embedded = false,
}: TreatmentOverviewHomeProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    isAdminFlow,
    isConsultationFlow,
    treatmentData,
    isLoading,
    isError,
    error,
    filters,
    providerTypeOptions,
    updateProviderType,
    updateAdviceType,
    updateDateRange,
    clearFilters,
    isUsingDefaultDateRange,
    defaultDateRange,
    selectedPatient,
    selectPatient,
    clearPatientSelection,
    canViewNotes,
  } = useTreatmentOverview({ appointmentId, patientId });

  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [activePdfUrl, setActivePdfUrl] = useState("");

  const handleViewNotes = useCallback(
    (item: TreatmentOverviewItem) => {
      if (!item.appointment_id) {
        toast({
          title: "Error",
          description: "Appointment ID not available for this record.",
          variant: "destructive",
        });
        return;
      }
      navigate(`/consultation/${item.appointment_id}`);
    },
    [navigate, toast]
  );

  const handleViewPdf = useCallback(
    (pdfUrl: string, item?: TreatmentOverviewItem) => {
      if (!pdfUrl) {
        toast({
          title: "Error",
          description: "PDF not available",
          variant: "destructive",
        });
        return;
      }
      if (isConsultationFlow && onOpenDocumentTab) {
        onOpenDocumentTab({
          id: item?.appointment_id || pdfUrl,
          description: item
            ? `${item.advice} - ${item.date}`
            : "Treatment Document",
          file: pdfUrl,
        });
        return;
      }
      setActivePdfUrl(pdfUrl);
      setPdfDialogOpen(true);
    },
    [toast, isConsultationFlow, onOpenDocumentTab]
  );

  const errorMessage =
    isError && error
      ? typeof error === "object" &&
        "message" in (error as unknown as Record<string, unknown>)
        ? String((error as unknown as Record<string, unknown>).message)
        : "Failed to load treatment data"
      : undefined;

  // Admin flow has no patient selected until search → date range filter is
  // meaningless because there's nothing to filter. Hide it until a patient is
  // chosen; consultation flow always has an implicit patient via appointmentId.
  const hasPatientContext =
    isConsultationFlow || !!selectedPatient || !!patientId;

  const filters_ = (
    <TreatmentOverviewFilters
      filters={filters}
      providerOptions={providerTypeOptions}
      onProviderTypeChange={updateProviderType}
      onAdviceTypeChange={updateAdviceType}
      onDateRangeChange={updateDateRange}
      onClearFilters={clearFilters}
      isUsingDefaultDateRange={isUsingDefaultDateRange}
      defaultDateRange={defaultDateRange}
      useDarkOverlays={false}
      showDateRange={hasPatientContext}
    />
  );

  const table = (
    <TreatmentOverviewTable
      data={treatmentData}
      isLoading={isLoading}
      isError={isError}
      isConsultationFlow={isConsultationFlow}
      errorMessage={errorMessage}
      withCard={!embedded}
      canViewNotes={canViewNotes}
      onViewNotes={handleViewNotes}
      onViewPdf={handleViewPdf}
    />
  );

  // Embedded layout (consultation flow) — BrowserWindow provides the outer card,
  // so render filters + table inline with a single divider between them.
  if (embedded) {
    return (
      <div className="flex min-h-0 flex-col overflow-hidden bg-card">
        {showHeader && (
          <div className="shrink-0 border-b border-border/60 px-5 py-4">
            <TreatmentOverviewHeader
              isConsultationFlow={isConsultationFlow}
              selectedPatient={selectedPatient}
              onSelectPatient={selectPatient}
              onClearPatient={clearPatientSelection}
            />
          </div>
        )}
        <div className="shrink-0 border-b border-border/60 px-5 py-3">
          {filters_}
        </div>
        {table}
        <PdfViewerDialog
          open={pdfDialogOpen}
          onOpenChange={setPdfDialogOpen}
          pdfUrl={activePdfUrl}
        />
      </div>
    );
  }

  // Standalone / admin layout — Card matching Patients directory style
  return (
    <div className="flex h-full flex-col gap-4">
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl border-border/60">
        {showHeader && (
          <CardHeader className="gap-4 border-b border-border/60 bg-card p-5">
            <TreatmentOverviewHeader
              isConsultationFlow={isConsultationFlow}
              selectedPatient={selectedPatient}
              onSelectPatient={selectPatient}
              onClearPatient={clearPatientSelection}
            />
            {filters_}
          </CardHeader>
        )}

        {!showHeader && (
          <div className="border-b border-border/60 bg-card p-5">
            {filters_}
          </div>
        )}

        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          <div className="min-h-0 flex-1 overflow-auto p-4">{table}</div>
        </CardContent>
      </Card>

      <PdfViewerDialog
        open={pdfDialogOpen}
        onOpenChange={setPdfDialogOpen}
        pdfUrl={activePdfUrl}
      />
    </div>
  );
};
